import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';

import { SortableTableRow } from './data-table-sortable-row';
import { MoneyDisplay } from './money-display';

/**
 * The table every list view uses.
 *
 * Three things it will not do, on purpose:
 * - **Render without pagination.** It takes one page of rows and shows a footer slot;
 *   it has no "show all" mode to reach for.
 * - **Format money itself.** A money column declares its currency and the cell renders
 *   through `MoneyDisplay`, so a table can never disagree with a report.
 * - **Sort client-side.** Sorting is a server concern, because sorting the fifty rows
 *   currently on screen is not sorting.
 *
 * Column order: when the row has a primary date (or date-time), that column is **first**
 * and must not use `hideBelowMd`. See documentation/frontend-design.md → Layout → Tables.
 *
 * Reference: documentation/architecture/widget-architecture.md §4.2.
 */

export type SortDirection = 'asc' | 'desc';

export interface DataTableColumn<Row> {
  /** Matches the server's allowlisted sort field when `sortable` is set. */
  id: string;
  header: ReactNode;
  /** Omit for a money column and give `money` instead. */
  cell?: (row: Row) => ReactNode;
  /** Renders the cell as an amount. `amount` is integer minor units. */
  money?: (
    row: Row,
  ) => {
    amount: number;
    currency: string;
    parenthesiseNegative?: boolean;
    colorBySign?: boolean;
  } | null;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  width?: string;
  /** Hidden below the medium breakpoint, for columns that are context, not content. */
  hideBelowMd?: boolean;
  /** Rendered beside a sortable header label, outside the sort button (e.g. a help tip). */
  headerAccessory?: ReactNode;
}

export interface DataTableProps<Row> {
  rows: Row[];
  columns: DataTableColumn<Row>[];
  rowKey: (row: Row) => string;
  loading?: boolean;
  /** Rendered in place of the table body when there are no rows and no loading. */
  empty?: ReactNode;
  onRowClick?: (row: Row) => void;
  /** Highlights a row without showing selection checkboxes. */
  highlightedRowKey?: string | null;
  /** Hover tooltip for the whole row. Return null/undefined to skip. */
  rowTooltip?: (row: Row) => ReactNode | null | undefined;
  rowTooltipPortal?: PopoverPortalTarget;
  /** Per-row action menu, rendered in a final column. */
  rowActions?: (row: Row) => ReactNode;
  selection?: {
    selected: Set<string>;
    onChange: (selected: Set<string>) => void;
    selectAllLabel: string;
    selectRowLabel: string;
    /** Rows that return false stay visible but cannot be ticked. */
    canSelect?: (row: Row) => boolean;
  };
  sort?: {
    field: string;
    direction: SortDirection;
    onChange: (field: string, direction: SortDirection) => void;
  };
  /** A totals row under the body — money columns only. */
  totals?: Record<
    string,
    { amount: number; currency: string } | Array<{ amount: number; currency: string }>
  >;
  totalsLabel?: string;
  actionsHeader?: string;
  className?: string;
  /** Drag handle column for manual row order. Server persists via `onReorder`. */
  reorder?: {
    handleLabel: string;
    onReorder: (orderedKeys: string[]) => void | Promise<void>;
    disabled?: boolean;
  };
}

const MOBILE_CARD_FIELD_LIMIT = 4;

const INTERACTIVE_ROW_CLICK_SELECTOR =
  'a, button, input, textarea, select, label, [role="button"], [role="menuitem"], [role="checkbox"]';

/** True when the event started on a control inside the row (name, Remove, checkbox). */
export const isInteractiveRowClickTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest(INTERACTIVE_ROW_CLICK_SELECTOR));

const renderColumnCell = <Row,>(column: DataTableColumn<Row>, row: Row) => {
  const money = column.money?.(row);
  if (money) {
    return (
      <MoneyDisplay
        amount={money.amount}
        currency={money.currency}
        parenthesiseNegative={money.parenthesiseNegative}
        colorBySign={money.colorBySign}
      />
    );
  }
  return column.cell?.(row);
};

export const DataTable = <Row,>({
  rows,
  columns,
  rowKey,
  loading = false,
  empty,
  onRowClick,
  highlightedRowKey,
  rowTooltip,
  rowTooltipPortal = 'body',
  rowActions,
  selection,
  sort,
  totals,
  totalsLabel,
  actionsHeader,
  className,
  reorder,
}: DataTableProps<Row>) => {
  const [hoveredTooltipKey, setHoveredTooltipKey] = useState<string | null>(null);
  const [optimisticRows, setOptimisticRows] = useState<Row[] | null>(null);

  const serverOrderKey = useMemo(() => rows.map(rowKey).join('\0'), [rows, rowKey]);

  useEffect(() => {
    if (reorder?.disabled) return;
    setOptimisticRows(null);
  }, [serverOrderKey, reorder?.disabled]);

  const displayRows = optimisticRows ?? rows;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!reorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentRows = optimisticRows ?? rows;
    const keys = currentRows.map(rowKey);
    const oldIndex = keys.indexOf(String(active.id));
    const newIndex = keys.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const newKeys = arrayMove(keys, oldIndex, newIndex);
    const rowByKey = new Map(currentRows.map((row) => [rowKey(row), row]));
    const reordered = newKeys.flatMap((key) => {
      const row = rowByKey.get(key);
      return row ? [row] : [];
    });
    if (reordered.length !== currentRows.length) return;

    setOptimisticRows(reordered);
    void Promise.resolve(reorder.onReorder(newKeys)).catch(() => {
      setOptimisticRows(null);
    });
  };

  const sortableIds = reorder ? displayRows.map((row) => rowKey(row)) : [];
  const mobileColumns = useMemo(
    () => columns.filter((column) => !column.hideBelowMd).slice(0, MOBILE_CARD_FIELD_LIMIT),
    [columns],
  );
  const useMobileCards = !reorder && mobileColumns.length > 0;

  const renderCells = (row: Row) =>
    columns.map((column) => {
      const money = column.money?.(row);
      return (
        <td
          key={column.id}
          className={cn(
            'px-3 py-2.5 text-fg',
            alignment(column),
            column.hideBelowMd && 'hidden md:table-cell',
          )}
        >
          {money ? (
            <MoneyDisplay
              amount={money.amount}
              currency={money.currency}
              parenthesiseNegative={money.parenthesiseNegative}
              colorBySign={money.colorBySign}
            />
          ) : (
            column.cell?.(row)
          )}
        </td>
      );
    });

  const renderRow = (row: Row) => {
    const key = rowKey(row);
    const isSelected = selection?.selected.has(key) ?? false;
    const isHighlighted = isSelected || key === highlightedRowKey;
    const clickable = Boolean(onRowClick);
    const tooltip = rowTooltip?.(row);
    const hasTooltip = Boolean(tooltip);

    const canSelectRow = selection?.canSelect?.(row) ?? true;
    const selectionCell = selection ? (
      <td className="px-3 py-2.5" onClick={(event) => event.stopPropagation()}>
        <Checkbox
          checked={canSelectRow && isSelected}
          disabled={!canSelectRow}
          onCheckedChange={(checked) => {
            if (!canSelectRow) return;
            const next = new Set(selection.selected);
            if (checked) next.add(key);
            else next.delete(key);
            selection.onChange(next);
          }}
          aria-label={selection.selectRowLabel}
        />
      </td>
    ) : null;

    const actionCell = rowActions ? (
      <td className="px-3 py-2.5 text-right" onClick={(event) => event.stopPropagation()}>
        {rowActions(row)}
      </td>
    ) : null;

    const rowClassName = cn(
      'border-t border-border transition-colors hover:bg-surface-sunken',
      isHighlighted && 'bg-brand/5',
      clickable && 'cursor-pointer focus-visible:bg-surface-sunken focus-visible:outline-none',
      hasTooltip && !clickable && 'cursor-help',
    );

    const rowProps = {
      onClick: clickable
        ? (event: MouseEvent<HTMLTableRowElement>) => {
            if (isInteractiveRowClickTarget(event.target)) return;
            onRowClick?.(row);
          }
        : undefined,
      onKeyDown: clickable
        ? (event: KeyboardEvent<HTMLTableRowElement>) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (isInteractiveRowClickTarget(event.target)) return;
            event.preventDefault();
            onRowClick?.(row);
          }
        : undefined,
      onPointerEnter: hasTooltip ? () => setHoveredTooltipKey(key) : undefined,
      onPointerLeave: hasTooltip
        ? () => setHoveredTooltipKey((current) => (current === key ? null : current))
        : undefined,
      tabIndex: clickable ? 0 : hasTooltip ? -1 : undefined,
      className: rowClassName,
    };

    const rowElement = reorder ? (
      <SortableTableRow
        id={key}
        handleLabel={reorder.handleLabel}
        disabled={reorder.disabled}
        {...rowProps}
      >
        {selectionCell}
        {renderCells(row)}
        {actionCell}
      </SortableTableRow>
    ) : (
      <tr {...rowProps}>
        {selectionCell}
        {renderCells(row)}
        {actionCell}
      </tr>
    );

    if (!hasTooltip) {
      return <Fragment key={key}>{rowElement}</Fragment>;
    }

    return (
      <Tooltip
        key={key}
        content={tooltip}
        popoverPortal={rowTooltipPortal}
        open={hoveredTooltipKey === key}
        onOpenChange={(next) => {
          if (!next) {
            setHoveredTooltipKey((current) => (current === key ? null : current));
          }
        }}
      >
        {rowElement}
      </Tooltip>
    );
  };

  const bodyRows = loading
    ? Array.from({ length: 6 }, (_, index) => (
        <tr key={index} className="border-t border-border">
          {reorder ? (
            <td className="px-2 py-3">
              <Skeleton className="mx-auto h-4 w-4" />
            </td>
          ) : null}
          {selection ? (
            <td className="px-3 py-3">
              <Skeleton className="h-4 w-4" />
            </td>
          ) : null}
          {columns.map((column) => (
            <td key={column.id} className="px-3 py-3">
              <Skeleton className="h-4 w-full max-w-32" />
            </td>
          ))}
          {rowActions ? <td className="px-3 py-3" /> : null}
        </tr>
      ))
    : displayRows.map((row) => renderRow(row));

  const tbody = reorder ? (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <tbody>{bodyRows}</tbody>
      </SortableContext>
    </DndContext>
  ) : (
    <tbody>{bodyRows}</tbody>
  );

  const selectableRows = displayRows.filter((row) => selection?.canSelect?.(row) ?? true);
  const allSelected =
    selection !== undefined &&
    selectableRows.length > 0 &&
    selectableRows.every((row) => selection.selected.has(rowKey(row)));
  const someSelected =
    selection !== undefined &&
    !allSelected &&
    selectableRows.some((row) => selection.selected.has(rowKey(row)));

  const toggleAll = () => {
    if (!selection || selectableRows.length === 0) return;
    const next = new Set(selection.selected);
    for (const row of selectableRows) {
      const key = rowKey(row);
      if (allSelected) next.delete(key);
      else next.add(key);
    }
    selection.onChange(next);
  };

  if (!loading && rows.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }

  const mobileTotals = totals && !loading && useMobileCards ? (
    <div className="space-y-1.5 rounded-lg border border-border bg-surface-sunken p-3 font-medium">
      {mobileColumns.map((column, index) => {
        const total = totals[column.id];
        if (!total && index !== 0) return null;
        return (
          <div
            key={column.id}
            className="flex items-start justify-between gap-3 text-body-sm"
          >
            <span className="text-fg-secondary">
              {index === 0 && !total ? totalsLabel : column.header}
            </span>
            <span className={cn('text-fg', alignment(column))}>
              {total ? (
                Array.isArray(total) ? (
                  <div className="flex flex-col items-end gap-0.5">
                    {total.map((entry) => (
                      <MoneyDisplay
                        key={entry.currency}
                        amount={entry.amount}
                        currency={entry.currency}
                      />
                    ))}
                  </div>
                ) : (
                  <MoneyDisplay amount={total.amount} currency={total.currency} />
                )
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  ) : null;

  const mobileCards = useMobileCards ? (
    <div className="flex flex-col gap-2 md:hidden">
      {selection ? (
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            disabled={selectableRows.length === 0}
            onCheckedChange={toggleAll}
            aria-label={selection.selectAllLabel}
          />
          <span className="text-body-sm text-fg-secondary">{selection.selectAllLabel}</span>
        </div>
      ) : null}

      {loading
        ? Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="rounded-lg border border-border bg-surface p-3">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="mt-2 h-4 w-1/2" />
              <Skeleton className="mt-2 h-4 w-2/3" />
            </div>
          ))
        : displayRows.map((row) => {
            const key = rowKey(row);
            const isSelected = selection?.selected.has(key) ?? false;
            const isHighlighted = isSelected || key === highlightedRowKey;
            const clickable = Boolean(onRowClick);
            const [titleColumn, ...detailColumns] = mobileColumns;

            const cardContent = (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    {selection ? (
                      <Checkbox
                        checked={(selection.canSelect?.(row) ?? true) && isSelected}
                        disabled={!(selection.canSelect?.(row) ?? true)}
                        onCheckedChange={(checked) => {
                          if (!(selection.canSelect?.(row) ?? true)) return;
                          const next = new Set(selection.selected);
                          if (checked) next.add(key);
                          else next.delete(key);
                          selection.onChange(next);
                        }}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={selection.selectRowLabel}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1 font-medium text-fg">
                      {titleColumn ? renderColumnCell(titleColumn, row) : null}
                    </div>
                  </div>
                  {rowActions ? (
                    <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                      {rowActions(row)}
                    </div>
                  ) : null}
                </div>
                {detailColumns.length > 0 ? (
                  <dl className="mt-2 space-y-1.5">
                    {detailColumns.map((column) => (
                      <div
                        key={column.id}
                        className="flex items-start justify-between gap-3 text-body-sm"
                      >
                        <dt className="shrink-0 text-fg-secondary">{column.header}</dt>
                        <dd className={cn('min-w-0 text-fg', alignment(column))}>
                          {renderColumnCell(column, row)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </>
            );

            const cardClassName = cn(
              'rounded-lg border border-border bg-surface p-3 transition-colors',
              isHighlighted && 'border-brand/30 bg-brand/5',
              clickable && 'cursor-pointer hover:bg-surface-sunken',
            );

            if (clickable) {
              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  className={cn(cardClassName, 'w-full text-left')}
                  onClick={(event) => {
                    if (isInteractiveRowClickTarget(event.target)) return;
                    onRowClick?.(row);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    if (isInteractiveRowClickTarget(event.target)) return;
                    event.preventDefault();
                    onRowClick?.(row);
                  }}
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <div key={key} className={cardClassName}>
                {cardContent}
              </div>
            );
          })}

      {mobileTotals}
    </div>
  ) : null;

  return (
    <div className={cn('w-full', className)}>
      {mobileCards}

      <div
        className={cn(
          'overflow-x-auto rounded-lg border border-border bg-surface',
          useMobileCards && 'hidden md:block',
        )}
      >
      <table className="w-full border-collapse text-body-sm">
        <thead className="sticky top-0 z-10 bg-surface-sunken">
          <tr>
            {reorder ? (
              <th scope="col" className="w-10 px-2 py-2.5">
                <span className="sr-only">{reorder.handleLabel}</span>
              </th>
            ) : null}

            {selection ? (
              <th scope="col" className="w-10 px-3 py-2.5">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  disabled={selectableRows.length === 0}
                  onCheckedChange={toggleAll}
                  aria-label={selection.selectAllLabel}
                />
              </th>
            ) : null}

            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                aria-sort={
                  sort?.field === column.id
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : column.sortable
                      ? 'none'
                      : undefined
                }
                className={cn(
                  'px-3 py-2.5 font-medium text-fg-secondary whitespace-nowrap',
                  alignment(column),
                  column.hideBelowMd && 'hidden md:table-cell',
                )}
              >
                {column.sortable && sort ? (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      column.align === 'right' && 'justify-end',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        sort.onChange(
                          column.id,
                          sort.field === column.id && sort.direction === 'asc'
                            ? 'desc'
                            : 'asc',
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-1 hover:text-fg',
                        column.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      {column.header}
                      <SortIcon
                        active={sort.field === column.id}
                        direction={sort.direction}
                      />
                    </button>
                    {column.headerAccessory}
                  </span>
                ) : (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      column.align === 'right' && 'justify-end',
                    )}
                  >
                    {column.header}
                    {column.headerAccessory}
                  </span>
                )}
              </th>
            ))}

            {rowActions ? (
              <th scope="col" className="w-12 px-3 py-2.5 text-right">
                <span className="sr-only">{actionsHeader}</span>
              </th>
            ) : null}
          </tr>
        </thead>

        {tbody}

        {totals && !loading ? (
          <tfoot className="border-t-2 border-border bg-surface-sunken font-medium">
            <tr>
              {reorder ? <td className="px-2 py-2.5" /> : null}
              {selection ? <td className="px-3 py-2.5" /> : null}
              {columns.map((column, index) => {
                const total = totals[column.id];
                return (
                  <td
                    key={column.id}
                    className={cn(
                      'px-3 py-2.5 text-fg',
                      alignment(column),
                      column.hideBelowMd && 'hidden md:table-cell',
                    )}
                  >
                    {total ? (
                      Array.isArray(total) ? (
                        <div className="flex flex-col items-end gap-0.5">
                          {total.map((entry) => (
                            <MoneyDisplay
                              key={entry.currency}
                              amount={entry.amount}
                              currency={entry.currency}
                            />
                          ))}
                        </div>
                      ) : (
                        <MoneyDisplay amount={total.amount} currency={total.currency} />
                      )
                    ) : index === 0 ? (
                      totalsLabel
                    ) : null}
                  </td>
                );
              })}
              {rowActions ? <td className="px-3 py-2.5" /> : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
      </div>
    </div>
  );
};

const alignment = <Row,>(column: DataTableColumn<Row>): string => {
  // Money is right-aligned without being asked, because a column of amounts that is
  // not right-aligned cannot be scanned for the largest one.
  const align = column.align ?? (column.money ? 'right' : 'left');
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
};

const SortIcon = ({ active, direction }: { active: boolean; direction: SortDirection }) => {
  if (!active) return <ChevronsUpDown aria-hidden className="h-3.5 w-3.5 opacity-40" />;
  return direction === 'asc' ? (
    <ArrowUp aria-hidden className="h-3.5 w-3.5" />
  ) : (
    <ArrowDown aria-hidden className="h-3.5 w-3.5" />
  );
};
